/**
 * Date and text formatting utilities
 */

import { format, parse, isValid, parseISO } from 'date-fns'

/**
 * Format date to localized string
 * @param {string|Date} date - Date to format
 * @param {string} formatStr - Format string (default: 'dd-MM-yyyy')
 * @returns {string} Formatted date string
 */
export const formatDate = (date, formatStr = 'dd/MM/yyyy') => {
    if (!date) return '-'

    try {
        let dateObj
        // If it's already a Date object
        if (date instanceof Date) {
            dateObj = date
        }
        // If it's a string in DD-MM-YYYY format
        else if (typeof date === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(date)) {
            dateObj = parse(date, 'dd-MM-yyyy', new Date())
        }
        // If it's an ISO string or other format
        else {
            dateObj = new Date(date)
            // If invalid, try parseISO
            if (!isValid(dateObj)) {
                dateObj = parseISO(date)
            }
        }

        if (!isValid(dateObj)) return 'Invalid Date'

        return format(dateObj, formatStr)
    } catch (error) {
        console.error('Date formatting error:', error)
        return 'Invalid Date'
    }
}

/**
 * Format date with time
 * @param {string|Date} date - Date to format
 * @param {string} formatStr - Format string (default: 'dd-MM-yyyy HH:mm')
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date, formatStr = 'dd/MM/yyyy HH:mm') => {
    return formatDate(date, formatStr)
}

/**
 * Capitalize first letter of string
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string
 */
export const capitalizeFirst = (str) => {
    if (!str) return ''
    return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Format status string (remove hyphens, capitalize)
 * @param {string} status - Status string
 * @returns {string} Formatted status
 */
export const formatStatus = (status) => {
    if (!status) return ''
    return status.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
}

/**
 * Format phone number
 * @param {string} phone - Phone number
 * @returns {string} Formatted phone number
 */
export const formatPhone = (phone) => {
    if (!phone) return ''
    // Format as (XXX) XXX-XXXX for 10 digit numbers
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
    }
    return phone
}

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
    if (!text || text.length <= maxLength) return text
    return text.slice(0, maxLength) + '...'
}

/**
 * Get current timestamp in ISO format with microseconds and timezone
 * Format: YYYY-MM-DDTHH:mm:ss.SSSSSS+05:30
 * @returns {string} Formatted timestamp
 */
export const getTimestampISO = () => {
    const now = new Date()

    const pad = (num, digits = 2) => num.toString().padStart(digits, '0')

    const year = now.getFullYear()
    const month = pad(now.getMonth() + 1)
    const day = pad(now.getDate())
    const hours = pad(now.getHours())
    const minutes = pad(now.getMinutes())
    const seconds = pad(now.getSeconds())
    const milliseconds = pad(now.getMilliseconds(), 3)
    const microseconds = '000' // JS doesn't support microseconds, padding with 0s

    const timezone = '+05:30'

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${microseconds}${timezone}`
}
